import { useEffect } from 'react';
import { ktmToday } from '../../utils/calendar';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import DualDatePicker from '../../components/common/DualDatePicker';
import { updateProfile, updateCalendarPreference } from '../../services/userService';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import { useCalendar } from '../../context/CalendarContext';
import { MdPerson, MdPhone, MdSave, MdCalendarMonth, MdPhotoCamera, MdBadge } from 'react-icons/md';
import AvatarUploader from '../../components/AvatarUploader';

function TrainerSettings() {
  document.title = 'Settings — TrainAxis';
  const { user, updateUser } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { name: user?.name || '', phone: user?.phone || '' },
  });
  const qc = useQueryClient();

  // Trainer profile (DOB / gender / address live on TrainerProfile, not User)
  const { data: profileData } = useQuery({
    queryKey: ['trainer-me'],
    queryFn:  () => api.get('/api/trainers/me').then(r => r.data),
  });
  const tp = profileData?.profile;
  const { register: regP, handleSubmit: submitP, control: ctrlP, reset: resetP } = useForm({
    defaultValues: { dateOfBirth: '', gender: '', 'address.street': '', 'address.city': '', 'address.district': '' },
  });
  useEffect(() => {
    if (tp) resetP({
      dateOfBirth:        tp.dateOfBirth ? String(tp.dateOfBirth).split('T')[0] : '',
      gender:             tp.gender || '',
      'address.street':   tp.address?.street || '',
      'address.city':     tp.address?.city || '',
      'address.district': tp.address?.district || '',
    });
  }, [tp, resetP]);

  const detailsMutation = useMutation({
    mutationFn: (d) => api.put('/api/trainers/me', {
      dateOfBirth: d.dateOfBirth || undefined,
      gender:      d.gender || undefined,
      address: { street: d['address.street'], city: d['address.city'], district: d['address.district'] },
    }),
    onSuccess: () => { toast.success('Personal details updated'); qc.invalidateQueries({ queryKey: ['trainer-me'] }); },
    onError:   () => toast.error('Failed to update details'),
  });

  const profileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: ({ data }) => {
      toast.success('Profile updated');
      if (data.user) updateUser({ name: data.user.name, phone: data.user.phone });
    },
    onError: () => toast.error('Failed to update profile'),
  });

  return (
    <div className="space-y-5 max-w-lg">
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <MdPhotoCamera className="text-primary text-xl" />
          <h2 className="font-semibold text-secondary text-sm">Profile Photo</h2>
        </div>
        <div className="flex items-center gap-4">
          <AvatarUploader
            currentPhotoUrl={user?.profilePhoto || null}
            studentName={user?.name}
            uploadUrl="/api/trainers/me/photo"
            size={96}
            onUploaded={(url) => { updateUser({ profilePhoto: url }); toast.success('Profile photo updated'); }}
          />
          <p className="text-xs text-gray-400 max-w-xs leading-relaxed">Click your photo to upload or change it.</p>
        </div>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-4">
          <MdPerson className="text-primary text-xl" />
          <h2 className="font-semibold text-secondary text-sm">My Profile</h2>
        </div>
        <form onSubmit={handleSubmit(d => profileMutation.mutate(d))} className="space-y-3">
          <Input label="Display Name" icon={MdPerson} error={errors.name?.message}
            {...register('name', { required: 'Name is required', minLength: { value: 2, message: 'At least 2 characters' } })} />
          <Input label="Phone" icon={MdPhone} type="tel" maxLength={10} error={errors.phone?.message}
            {...register('phone', { pattern: { value: /^(97|98)\d{8}$/, message: 'Enter a valid Nepal mobile number' } })} />
          <Button type="submit" loading={profileMutation.isPending}>
            <MdSave /> Save Profile
          </Button>
        </form>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-4">
          <MdBadge className="text-primary text-xl" />
          <h2 className="font-semibold text-secondary text-sm">Personal Details</h2>
        </div>
        <form onSubmit={submitP(d => detailsMutation.mutate(d))} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date of Birth</label>
            <Controller name="dateOfBirth" control={ctrlP}
              render={({ field }) => (
                <DualDatePicker value={field.value || ''} onChange={field.onChange} max={ktmToday()} fastNav />
              )} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Gender</label>
            <select {...regP('gender')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary">
              <option value="">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <Input label="Street / Tole" {...regP('address.street')} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="City" {...regP('address.city')} />
            <Input label="District" {...regP('address.district')} />
          </div>
          <Button type="submit" loading={detailsMutation.isPending}>
            <MdSave /> Save Details
          </Button>
        </form>
      </Card>

      {/* Theme buttons removed — the sun/moon toggle in the top navbar now owns this, writing
          through the same applyTheme/updateTheme path used everywhere else. */}
      <p className="text-xs text-subtle">
        Dashboard theme is switched from the sun/moon icon in the top bar.
      </p>

      {/* Calendar Preference card removed (Step 2): dates now render Bikram Sambat primary with
          a muted AD secondary everywhere — no per-user choice. */}
    </div>
  );
}

export default TrainerSettings;
